'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from "@/i18n/navigation";
import { Headings } from './../../components';
import { CheckCircleIcon, ShieldCheckIcon, ClockIcon } from "@heroicons/react/24/solid";
import { trackEvent } from "../../lib/tracking";
import LazyOnView from "../../lib/LazyOnView";
import PhoneCallButton from "../Tracking/PhoneCallButton";
import PhotoUpload from "./PhotoUpload";

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full relative border border-neutral-200">
        <button
          className="absolute top-3 right-3 h-9 w-9 rounded-full border border-neutral-200 hover:bg-neutral-50 transition flex items-center justify-center text-neutral-700"
          onClick={onClose}
          aria-label="Fermer"
          type="button"
        >
          &#x2715;
        </button>
        {children}
      </div>
    </div>
  );
};

const AlertModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col items-center text-center">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-sand border border-sand-line">
          <CheckCircleIcon className="h-7 w-7 text-cta-700" />
        </div>

        <h2 className="mt-4 text-xl font-extrabold text-brand-900">
          Merci, votre demande est envoyée !
        </h2>
        <p className="mt-2 text-neutral-600 leading-relaxed">
          Un de nos experts vous recontacte le plus rapidement possible.
        </p>

        <button
          className="mt-6 px-6 py-2.5 bg-cta-700 text-white rounded-2xl font-semibold hover:bg-cta-800 transition"
          onClick={onClose}
          type="button"
        >
          Ok
        </button>
      </div>
    </Modal>
  );
};

const FormViewTracker = () => {
  useEffect(() => {
    trackEvent("form_view");
  }, []);

  return null;
};

const Contact = () => {
  const [buttonText, setButtonText] = useState('Envoyer');
  const [isDisabled, setIsDisabled] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [photoUploadKey, setPhotoUploadKey] = useState(0);
  const formStartedRef = useRef(false);

  const handleFormFocus = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent("form_start");
  };

  const handlePhoneBlur = (event) => {
    if (event.target.value.trim()) {
      trackEvent("phone_filled");
    }
  };

  const trackGaEvent = (eventName, params = {}) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, {
        page_location: window.location.href,
        page_title: document.title,
        ...params,
      });
    } else {
      console.warn(`gtag n'est pas disponible pour l'événement : ${eventName}`);
    }
  };

  const trackFormConversion = (formData) => {
    trackGaEvent("send_contact_form_event", {
      form_name: "contact_form",
      contact_method: "form",
      commune: formData.get("form_commune") || "",
      type_bien: formData.get("form_type_bien") || "",
      urgence: formData.get("form_urgence") || "",
      debug_mode: true,
    });

    trackEvent("form_submit", {
      commune: formData.get("form_commune") || "",
      typeBien: formData.get("form_type_bien") || "",
      urgence: formData.get("form_urgence") || "",
    });
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    setIsDisabled(true);
    setButtonText("Envoi...");

    const form = event.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('/api/mail', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `response status: ${response.status}`);
      }

      trackFormConversion(formData);

      setModalOpen(true);
      form.reset();
      setPhotoUploadKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      alert("Erreur. Merci de réessayer.");
    } finally {
      setIsDisabled(false);
      setButtonText("Envoyer");
    }
  };

  const inputBase =
    "mt-2 w-full rounded-2xl border border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-900 outline-none " +
    "focus:ring-2 focus:ring-accent/35 focus:border-accent transition";

  return (
    <section id="contactus" className="py-20 bg-white relative">
      <div
        className="pointer-events-none absolute inset-0 opacity-55"
        style={{
          background:
            "radial-gradient(900px 500px at 12% 0%, rgba(193,98,44,0.08), transparent 60%), radial-gradient(900px 500px at 88% 15%, rgba(224,164,88,0.10), transparent 55%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <Headings title="Votre Devis Gratuit" />

        <p className="mt-4 text-neutral-600 max-w-3xl">
          Décrivez votre besoin, nous vous rappelons rapidement.{" "}
          <span className="font-semibold text-neutral-900">
            Devis gratuit • Sans engagement
          </span>{" "}
          • Bruxelles et ses 19 communes.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:items-stretch">
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-neutral-200 shadow-[0_10px_30px_rgba(0,0,0,0.08)] p-7 md:p-8">
              <LazyOnView>
                <FormViewTracker />
              </LazyOnView>

              <form id="contact_form" onSubmit={sendEmail} onFocusCapture={handleFormFocus}>
                <input
                  type="text"
                  name="form_website"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-1">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Nom *
                      </span>
                      <input
                        id="nom"
                        name="form_nom"
                        type="text"
                        placeholder="Votre nom"
                        className={inputBase}
                        required
                      />
                    </label>
                  </div>

                  <div className="md:col-span-1">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Téléphone *
                      </span>
                      <input
                        id="tel"
                        name="form_tel"
                        type="tel"
                        placeholder="+32 …"
                        className={inputBase}
                        required
                        autoComplete="tel"
                        onBlur={handlePhoneBlur}
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-sm font-semibold text-neutral-900">
                      Email (optionnel)
                    </span>
                    <input
                      id="email"
                      name="form_email"
                      type="email"
                      placeholder="email@exemple.com"
                      className={inputBase}
                      autoComplete="email"
                    />
                  </label>

                  <div className="md:col-span-1">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Commune du bien *
                      </span>
                      <input
                        id="commune"
                        name="form_commune"
                        type="text"
                        placeholder="Ixelles, Uccle, Schaerbeek…"
                        className={inputBase}
                        required
                      />
                    </label>
                  </div>

                  <div className="md:col-span-1">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Type de bien
                      </span>
                      <select
                        name="form_type_bien"
                        className={inputBase}
                        defaultValue="Maison"
                      >
                        <option>Maison</option>
                        <option>Appartement</option>
                        <option>Cave</option>
                        <option>Grenier</option>
                        <option>Garage</option>
                        <option>Bureau / Local</option>
                        <option>Autre</option>
                      </select>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Urgence
                      </span>
                      <select
                        name="form_urgence"
                        className={inputBase}
                        defaultValue="Cette semaine"
                      >
                        <option>Urgent (24–48h)</option>
                        <option>Cette semaine</option>
                        <option>Flexible</option>
                      </select>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block">
                      <span className="text-sm font-semibold text-neutral-900">
                        Message *
                      </span>
                      <textarea
                        id="description"
                        name="message"
                        rows="5"
                        className={inputBase}
                        placeholder="Décrivez le volume, l'accès, étage/ascenseur, objets à valoriser…"
                        required
                      />
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <span className="text-sm font-semibold text-neutral-900">
                      Photos (optionnel)
                    </span>
                    <div className="mt-2">
                      <PhotoUpload
                        key={photoUploadKey}
                        name="form_photos_link"
                        onUploadingChange={setPhotosUploading}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-3">
                  <input
                    id="link-checkbox"
                    type="checkbox"
                    required
                    className="mt-1 h-5 w-5 accent-ink"
                  />
                  <label
                    htmlFor="link-checkbox"
                    className="text-sm text-neutral-600"
                  >
                    J'accepte la{" "}
                    <Link
                      href="/politique-de-confidentialite"
                      target="_blank"
                      className="font-semibold text-neutral-900 underline decoration-accent underline-offset-4 hover:opacity-80 transition"
                      rel="noreferrer"
                    >
                      politique de confidentialité
                    </Link>
                    .
                  </label>
                </div>

                {/* CTA */}
                <div className="mt-8">
                  <button
                    id="send_form"
                    type="submit"
                    disabled={isDisabled || photosUploading}
                    className="group inline-flex h-[58px] w-full items-center justify-center gap-3 rounded-2xl bg-cta-700 px-8 text-[17px] font-bold text-white shadow-cta transition-all duration-300 hover:-translate-y-0.5 hover:bg-cta-800 active:translate-y-0 disabled:opacity-60"
                  >
                    {photosUploading ? "Envoi des photos..." : buttonText}
                  </button>
                </div>

                <p className="mt-4 text-xs text-neutral-500">
                  Réponse rapide • Devis gratuit • Intervention planifiée
                </p>

                <AlertModal
                  isOpen={isModalOpen}
                  onClose={() => setModalOpen(false)}
                />
              </form>
            </div>
          </div>

          <div className="flex flex-col lg:col-span-5">
            <div className="rounded-3xl border border-neutral-200 bg-brand-900 text-white shadow-sm overflow-hidden">
              <div className="p-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 text-xs font-semibold text-white">
                  <CheckCircleIcon className="h-4 w-4 text-white/80" />
                  Devis gratuit • Sans engagement
                </div>

                <h3 className="mt-4 text-xl md:text-2xl font-serif font-extrabold">
                  On vous rappelle rapidement
                </h3>
                <p className="mt-2 text-white/75">
                  Expliquez votre besoin, nous vous répondons au plus vite.
                </p>
              </div>

              <div className="border-t border-white/10 p-7 space-y-5">
                <div className="flex items-start gap-3">
                  <ClockIcon className="h-6 w-6 shrink-0 text-white/80" />
                  <div>
                    <p className="font-bold">Réponse rapide</p>
                    <p className="text-sm text-white/70">Un premier retour sous 24h ouvrées.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheckIcon className="h-6 w-6 shrink-0 text-white/80" />
                  <div>
                    <p className="font-bold">Sans engagement</p>
                    <p className="text-sm text-white/70">Devis gratuit, vous restez libre de votre décision.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 shrink-0 text-white/80" />
                  <div>
                    <p className="font-bold">Rachat d'objets</p>
                    <p className="text-sm text-white/70">Objets de valeur repris et déduits du devis.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-1 items-center justify-center pb-16">
              <PhoneCallButton
                location="contact_section"
                className="group inline-flex h-[58px] w-fit items-center justify-center gap-3 rounded-full border-2 border-cta-700 bg-white px-8 text-[17px] font-bold text-ink shadow-[0_10px_30px_rgba(0,0,0,0.10)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-cta-50 active:translate-y-0"
              >
                Appeler maintenant
              </PhoneCallButton>
            </div>
          </div>
        </div>

        <div className="mt-14 h-[3px] w-full rounded-full bg-gradient-to-r from-sand-line via-accent to-sand-line" />
      </div>
    </section>
  );
};

export default Contact;
